import { useRef, type KeyboardEvent } from 'react';
import { ORDER_TABS, nextOrderTab, orderPanelId, orderTabId, type OrderTabId } from './orderTabIds';

interface OrderTabsProps {
    baseId: string;
    activeTab: OrderTabId;
    scansCount: string | null;
    onChange: (tab: OrderTabId) => void;
}

export function OrderTabs({ baseId, activeTab, scansCount, onChange }: OrderTabsProps) {
    const listRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = nextOrderTab(activeTab, event.key === 'ArrowRight' ? 1 : -1);
        onChange(next);
        listRef.current?.querySelector<HTMLElement>(`#${orderTabId(baseId, next)}`)?.focus();
    };

    return (
        <div
            ref={listRef}
            role="tablist"
            aria-label="Order"
            onKeyDown={handleKeyDown}
            className="flex h-10 shrink-0 items-stretch gap-5 border-b border-gray-200 bg-white px-5"
        >
            {ORDER_TABS.map((tab) => {
                const isActive = tab.id === activeTab;
                const count = tab.id === 'scans' ? scansCount : null;
                return (
                    <button
                        key={tab.id}
                        id={orderTabId(baseId, tab.id)}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={orderPanelId(baseId, tab.id)}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(tab.id)}
                        className={`relative inline-flex items-center gap-1.5 rounded-sm text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400 ${
                            isActive
                                ? 'font-semibold text-gray-900'
                                : 'font-medium text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        {tab.label}
                        {count !== null && (
                            <span className="text-[12px] font-medium tabular-nums text-gray-400">
                                {count}
                            </span>
                        )}
                        {isActive && (
                            <span
                                aria-hidden="true"
                                className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-gray-900"
                            />
                        )}
                    </button>
                );
            })}
        </div>
    );
}
